import { useInfiniteQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { fromNotificationWire } from '@/notifications/lib/notification-normalizer';
import {
  DEFAULT_PAGE_SIZE,
  URL_USER_NOTIFICATIONS,
} from '@/notifications/lib/notification-constants';
import type {
  NotificationModel,
  NotificationModelWire,
} from '@/shared/types/notifications';

/**
 * Paginated notifications list for the Notification Center panel.
 *
 *   POST /api/Notifications/UserNotifications
 *   body: { pageNumber: number, pageSize: number }
 *   response: NotificationModelWire[]
 *
 * Mirrors Flutter `notification_center_data_source_impl.dart:24-35`.
 *
 * **Flutter divergence (orchestrator-approved 2026-05-21, Q-A)**: Flutter
 * `notification_center_screen.dart:44` uses `pageSize: 1` on the initial
 * fetch — a perf hack that breaks the bloc's own `hasMoreItems`
 * calculation (which compares to `defaultPageSize` at bloc:84). React
 * drops the quirk and uses real `DEFAULT_PAGE_SIZE` from the start. See
 * CONVERSION_NOTES §13 for the verdict.
 *
 * **Pagination contract**: Flutter bloc considers the list exhausted
 * when a page returns `< defaultPageSize` items (bloc:84-88). React
 * mirrors via React Query's `getNextPageParam`.
 *
 * Wire response is normalized via `fromNotificationWire` to camelCase
 * (only drift is `data.AppointmentId` per dental-list precedent).
 */

export const NOTIFICATIONS_LIST_KEY = ['notifications', 'list'] as const;

export interface UseUserNotificationsParams {
  pageSize?: number;
}

export function useUserNotifications(params: UseUserNotificationsParams = {}) {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_LIST_KEY, pageSize] as const,
    initialPageParam: 1,
    queryFn: async ({
      pageParam,
      signal,
    }: {
      pageParam: number;
      signal: AbortSignal;
    }): Promise<NotificationModel[]> => {
      const wire = await post<NotificationModelWire[]>(
        URL_USER_NOTIFICATIONS,
        { pageNumber: pageParam, pageSize },
        { signal },
      );
      return wire.map(fromNotificationWire);
    },
    getNextPageParam: (lastPage, allPages) => {
      // Flutter bloc:84-88 — has more iff last page filled completely.
      if (lastPage.length < pageSize) return undefined;
      return allPages.length + 1;
    },
  });
}
